import { Component, OnInit } from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { UtilsService } from "../../../../auth/service/utils.service";
import { AuthenticationService } from "../../../../auth/service";
import { locale as italian } from "../../../../i18n/it";
import { Router } from "@angular/router";
import { IContentHeader } from "../../../../layout/components/content-header/content-header.component";
import { ListService } from "../../../../auth/service/list.service";

interface IPaymentTerms {
  name: string;
  id?: number;
  other: {
    day_value: number;
    payment_end_month: boolean;
    code: string;
    payment_type: string;
    payment_type_name?: string;
  };
}

@Component({
  selector: "app-payment-terms-list",
  templateUrl: "./payment-terms-list.component.html",
  styles: [""]
})
export class PaymentTermsListComponent implements OnInit {
  public tableLimit: number = 10;
  public searchKeyword: string = "";
  public contentHeader: IContentHeader;
  public paymentTerms: IPaymentTerms = {
    name: null,
    other: {
      day_value: null,
      payment_end_month: false,
      code: null,
      payment_type: null
    }
  };
  public paymentTermsList: IPaymentTerms[] = [];
  public filteredPaymentTermsList: IPaymentTerms[] = [];
  public showModalError: boolean = false;

  public paymentTypes: { value: string; label: string; }[] = [
    { value: "01", label: "Rimessa diretta" },
    { value: "02", label: "Ri.Ba" },
    { value: "04", label: "Bonifico" },
    { value: "15", label: "RID" }
  ];

  constructor(
    public _modalService: NgbModal,
    public _utilService: UtilsService,
    public _authenticationService: AuthenticationService,
    private router: Router,
    public _listService: ListService
  ) {
    this.contentHeader = {
      headerTitle: italian.data.MENU.PAYMENT_TERMS,
      actionButton: false,
      breadcrumb: {
        type: "",
        links: [
          {
            name: "Home",
            isLink: true,
            link: "/"
          },
          {
            name: italian.data.MENU.MANAGE,
            isLink: true,
            link: "/dashboard/platform-management"
          },
          {
            name: italian.data.MENU.PAYMENT_TERMS,
            isLink: false
          }
        ]
      }
    };
  }

  async ngOnInit() {
    if (!this._authenticationService.hasCapability("show_menu_payment_terms_list")) {
      this._utilService.hideLoading();
      await this.router.navigateByUrl("/dashboard/main");
      return false;
    }
    await this.loadPaymentTerms();
  }

  async loadPaymentTerms() {
    this.paymentTermsList = await this._listService.getList("payment_terms")
      .then((data: IPaymentTerms[]) => {
        data.map((item: IPaymentTerms) => {
          const paymentType = this.paymentTypes.find((type) => type.value === (item.other.payment_type || "").toString());
          if (paymentType) {
            item.other.payment_type_name = paymentType.label;
          }
          return item;
        });
        return data;
      });
    this.paymentTermsFilter();
  }

  updateTableLimit(event: { target: { value: string } }) {
    this.tableLimit = Number(event.target.value);
  }

  paymentTermsFilter() {
    let items: IPaymentTerms[] = this.paymentTermsList;
    const keywords: string = this.searchKeyword.trim().toLowerCase();

    if (keywords.length > 0) {
      items = this.paymentTermsList.filter((item: IPaymentTerms) => item.name.toLowerCase().includes(this.searchKeyword.trim().toLowerCase()));
    }

    this.filteredPaymentTermsList = items;
  }

  public modalOpen(whichModal, paymentTerms: IPaymentTerms) {
    this.showModalError = false;
    this.paymentTerms = {
      name: null,
      other: {
        day_value: null,
        payment_end_month: false,
        code: null,
        payment_type: null
      }
    };
    if (!!paymentTerms) {
      this.paymentTerms = {
        id: paymentTerms.id,
        name: paymentTerms.name,
        other: {
          day_value: paymentTerms.other.day_value,
          payment_end_month: paymentTerms.other.payment_end_month,
          code: paymentTerms.other.code,
          payment_type: paymentTerms.other.payment_type
        }
      };
    }
    this._modalService.open(whichModal, {
      size: "sm",
      centered: true
    });
  }

  isError(): boolean {
    if (
      !this.paymentTerms.name ||
      this.paymentTerms.name.trim() === "" ||
      this.paymentTerms.name.length < 1 ||
      !this.paymentTerms.other.day_value ||
      this.paymentTerms.other.day_value > 365 ||
      this.paymentTerms.other.day_value < 1
    ) {
      this.showModalError = true;
      return true;
    }
  }

  async deletePaymentTerms(currentPaymentTerms: { name: string; id?: number }) {
    if (!this._authenticationService.hasCapability("delete_payment_terms")) {
      this._utilService.hideLoading();
      await this.router.navigateByUrl("/dashboard/main");
      return false;
    }

    let deleteText: string = await this._utilService.translate("ALERT.CONFIRM_DELETE_TEXT");
    deleteText += await this._utilService.translate("ALERT.CONFIRM_DELETE_THIS_OBJECT");
    deleteText += currentPaymentTerms.name;

    await this._utilService.showSWALDeleteConfirmation({ text: deleteText }).then(async (result) => {
      if (result.value) {
        await this._listService
          .delete(currentPaymentTerms.id, "payment_terms")
          .then(async () => {
            await this._utilService.showPopupItemDeleted().then(async () => {
              await this.loadPaymentTerms();
            });
          })
          .catch(() => this._utilService.showSWALError({ text: "ERROR_PAYMENT_TERMS_REMOVE" }));
      } else if (result.isDismissed) {
        await this._utilService.showSWALError({
          title: "ABORTED_TITLE",
          text: "ABORTED_TEXT"
        });
      }
    });
  }

  async addPaymentTerms() {
    if (!this._authenticationService.hasCapability("create_payment_terms")) {
      this._utilService.hideLoading();
      await this.router.navigateByUrl("/dashboard/main");
      return false;
    }

    if (this.isError()) {
      return false;
    }

    this._utilService.showLoading();
    await this._listService
      .create(
        {
          name: this.paymentTerms.name.trim(),
          other: {
            ...this.paymentTerms.other,
            payment_type: (this.paymentTerms.other.payment_type as any).value
          }
        },
        "payment_terms")
      .then(() => {
        this.loadPaymentTerms();
        this._modalService.dismissAll();
        this._utilService.hideLoading();
        this.paymentTermsFilter();
        this._utilService.showSWALSuccess({ text: "SUCCESS_UPLOAD_PAYMENT_TERMS" });
      })
      .catch(() => {
        this._utilService.hideLoading();
        this._utilService.showSWALError({ text: "ERROR_PAYMENT_TERMS_INSERT" });
      });
  }

  async editPaymentTerms() {
    if (!this._authenticationService.hasCapability("edit_payment_terms")) {
      this._utilService.hideLoading();
      await this.router.navigateByUrl("/dashboard/main");
      return false;
    }

    if (this.isError()) {
      return false;
    }

    const data: IPaymentTerms = {
      name: this.paymentTerms.name.trim(),
      other: {
        day_value: this.paymentTerms.other.day_value,
        payment_end_month: this.paymentTerms.other.payment_end_month,
        code: this.paymentTerms.other.code,
        payment_type: this.paymentTerms.other.payment_type
      }
    };

    this._utilService.showLoading();
    await this._listService
      .update(this.paymentTerms.id, data, "payment_terms")
      .then(() => {
        this._utilService.hideLoading();
        this.loadPaymentTerms();
        this.paymentTermsFilter();
        this._modalService.dismissAll();
        this._utilService.showSWALSuccess({ text: "SUCCESS_EDIT_PAYMENT_TERMS" });
      })
      .catch(() => {
        this._utilService.hideLoading();
        this._utilService.showSWALError({ text: "ERROR_PAYMENT_TERMS_EDIT" });
      });
  }
}
